import {
  add,
  format as dateFnsFormat,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
  startOfMonth,
  sub
} from 'date-fns'
import { de } from 'date-fns/locale'
import moment from 'moment/moment'
import { format as doFormatTimeAgo } from 'timeago.js/lib/format'
import { register as registerTimeAgo } from 'timeago.js/lib/register'

moment.locale('de')

export function useDate() {
  const TIMESPAN = {
    DAY: 'day',
    MONTH: 'month'
  }

  const DATE_FORMATS_MOMENT = {
    DEFAULT: 'DD.MM.YYYY HH:mm',
    YEAR: 'YYYY',
    MONTH: 'MMMM',
    MONTH_NUMERIC: 'M',
    YY: 'Y',
    DATE: 'DD.MM.YYYY',
    DATE_SHORT: 'dd DD.MM',
    DATE_EN: 'YYYY-MM-DD',
    HOUR: 'HH:mm',
  }

  const DATE_FORMATS_FNS = {
    DEFAULT: 'dd.MM.yyyy HH:mm',
    YEAR: 'yyyy',
    MONTH: 'MMMM',
    MONTH_NUMERIC: 'M',
    YY: 'Y',
    DATE: 'dd.MM.yyyy',
    DATE_SHORT: 'E dd.MM',
    DATE_EN: 'yyyy-MM-dd',
    HOUR: 'HH:mm',
  }

  const getDateFormat = (key, source = 'moment') => {
    // Prepare migrating everything to date fns.
    key = key.toUpperCase()
    if (source === 'moment') {
      return DATE_FORMATS_MOMENT[key.toUpperCase()]
    } else {
      return DATE_FORMATS_FNS[key.toUpperCase()]
    }
  }

  const timeAgoLabels: [string, string][] = [
    ['gerade eben', 'jetzt gleich'],
    ['vor %s Sekunden', 'in %s Sekunden'],
    ['vor 1 Minute', 'in 1 Minute'],
    ['vor %s Minuten', 'in %s Minuten'],
    ['vor 1 Stunde', 'in 1 Stunde'],
    ['vor %s Stunden', 'in %s Stunden'],
    ['gestern', 'morgen'],
    ['vor %s Tagen', 'in %s Tagen'],
    ['vor 1 Woche', 'in 1 Woche'],
    ['vor %s Wochen', 'in %s Wochen'],
    ['vor 1 Monat', 'in 1 Monat'],
    ['vor %s Monaten', 'in %s Monaten'],
    ['vor 1 Jahr', 'in 1 Jahr'],
    ['vor %s Jahren', 'in %s Jahren'],
  ];

  registerTimeAgo('de_DE', function (number: number, index: number): [string, string] {
    // Parameters:
    // number: the time ago / time in number;
    // index: the index of array below;
    // totalSec: total seconds between date to be formatted and today's date;
    return timeAgoLabels[index] ?? ["unbekannt", "unbekannt"];
  })

  function formatDate(raw, format = 'default') {
    return moment(raw).format(getDateFormat(format, 'moment'))
  }

  function formatWithDateFns(raw, format = 'default') {
    return dateFnsFormat(raw, getDateFormat(format, 'fns'), { locale: de })
  }

  function formatTimeAgo(date) {
    return doFormatTimeAgo(moment(date).toDate(), 'de_DE')
  }

  function formatCancellationDate(id) {
    if (!id) {
      return 'unbekannt'
    }
    return {
      0: 'Sofort kündbar',
      5: 'Täglich',
      1: 'Ende der Woche',
      2: 'Ende des Monats',
      3: 'Ende des nächsten Jahres',
      4: 'Ende des Jahres',
    }[id]
  }

  function getMomentByTime(time) {
    const parts = time.split(':')
    return moment().hour(parts[0]).minute(parts[1]).second(0)
  }

  // Adds 1 minute to a given time and returns the result.
  // Format: HH:MM
  function nextMinute(oldTime) {
    const parts = oldTime.split(':')
    return moment()
      .hour(parts[0])
      .minute(parts[1])
      .second(0)
      .add(1, 'minute')
      .format('HH:mm')
  }

  // Subtracts 1 minute to a given time and returns the result.
  // Format: HH:MM
  function prevMinute(oldTime) {
    const parts = oldTime.split(':')
    return moment()
      .hour(parts[0])
      .minute(parts[1])
      .second(0)
      .subtract(1, 'minute')
      .format('HH:mm')
  }

  function formatPriceDate(date) {
    return moment(date).format('DD.MM.YYYY')
  }

  // For convenience our date functions default to the current date.
  // To be consistent allow using all functions in the same way, these simple
  // wrappers have been created.
  function customStartOfDay(date = new Date()) {
    return startOfDay(date)
  }

  function today(date = new Date()) {
    return customStartOfDay(date)
  }

  function tomorrow(date = new Date()) {
    return startOfDay(applyOffset(date, 1, 'day'))
  }

  function yesterday(date = new Date()) {
    return startOfDay(applyOffset(date, -1, 'day'))
  }

  function customStartOfMonth(date = new Date()) {
    return startOfMonth(date)
  }

  function nextMonth(date = new Date()) {
    return startOfMonth(applyOffset(date, 1, 'month'))
  }

  function lastMonth(date = new Date()) {
    return startOfMonth(applyOffset(date, -1, 'month'))
  }

  function isBeforeOrEqual(date: Date, referenceDate: Date) {
    return isBefore(date, referenceDate) || isEqual(date, referenceDate)
  }

  function isAfterOrEqual(date: Date, referenceDate: Date) {
    return isAfter(date, referenceDate) || isEqual(date, referenceDate)
  }

  /**
   * Returns a new date object after applying the given offset.
   *
   * Params:
   * - date: The date object
   * - offset: Number of [unit] to add/subtract from the given date.
   * - unit: The unit - one of 'years', 'months', 'weeks', 'days', 'hours',
   *         'minutes' or 'seconds'.
   */
  function applyOffset(date: Date, offset: number, unit: string) {
    const duration = {}
    // The date modification functions of date-fns expect the units to be
    // plurals. But to gracefully allow singular units we add the missing 's'
    // if necessary. None of the time unit singulars end with an 's'.
    if (unit.slice(-1) !== 's') {
      unit += 's'
    }
    duration[unit] = Math.abs(offset)
    if (offset < 0) {
      return sub(date, duration)
    } else {
      return add(date, duration)
    }
  }

  return {
    TIMESPAN,
    DATE_FORMATS_MOMENT,
    DATE_FORMATS_FNS,
    getDateFormat,
    formatDate,
    formatWithDateFns,
    formatTimeAgo,
    formatCancellationDate,
    getMomentByTime,
    prevMinute,
    nextMinute,
    formatPriceDate,
    isBeforeOrEqual,
    isBefore,
    isAfter,
    isAfterOrEqual,
    startOfDay: customStartOfDay,
    startOfMonth: customStartOfMonth,
    add,
    sub,
    subtract: sub,
    applyOffset,
    today,
    tomorrow,
    yesterday,
    nextMonth,
    lastMonth,
    moment,
  }
}
